import React from "react";
import { Loader2, AlertCircle, CalendarClock, ArrowRight } from "lucide-react";
import { money } from "../../lib/packageLogic";

export const DISCLAIMER =
  "This is an indicative price based on what you've told us. We'll confirm it on a short Discovery Call once we understand your business properly.";

// The engine can return zero when the optional volume questions are left blank.
// Showing a visitor "£0 per month" is worse than showing nothing, so we treat it
// as an incomplete answer set and ask for more detail instead.
export const hasPrice = (q) => ((q?.totals?.monthly_recurring || 0) + (q?.totals?.one_off || 0)) > 0;

const Indicative = () => (
  <span className="inline-block text-[10px] font-bold uppercase tracking-widest bg-orange/15 text-orange-dark px-2 py-1 rounded">
    Indicative
  </span>
);

// Slim sticky bar for small screens, where the rail can't sit alongside.
// Same rule as the rail: progress and a way forward, never a live figure.
export const QuoteBar = ({ quote, quoting, count = 0, remaining = 0, onSeePrice, onJump }) => {
  const ready = quote && (quote.status === "priced" || quote.status === "priced_review") && hasPrice(quote);
  return (
    <div
      className="lg:hidden fixed bottom-0 left-0 right-0 z-40 border-t border-slate-200 bg-white/95 backdrop-blur-md shadow-[0_-4px_16px_rgba(15,23,42,0.08)]"
      data-testid="package-quote-bar"
    >
      <div className="max-w-7xl mx-auto px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] flex items-center gap-3">
        <div className="min-w-0">
          <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">Your package</p>
          <p className="text-sm font-bold text-navy truncate" data-testid="package-bar-count">
            {count} service{count === 1 ? "" : "s"} selected
          </p>
        </div>
        <div className="ml-auto shrink-0">
          {ready ? (
            <button
              type="button"
              onClick={onSeePrice}
              data-testid="package-bar-see-price"
              className="inline-flex items-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-5 py-2.5 rounded-full transition-colors"
            >
              See your price <ArrowRight size={15} />
            </button>
          ) : quoting ? (
            <span className="inline-flex items-center gap-2 text-xs font-semibold text-slate-500" data-testid="package-bar-status">
              <Loader2 size={14} className="animate-spin text-orange" /> Checking…
            </span>
          ) : remaining > 0 ? (
            <button
              type="button"
              onClick={onJump}
              data-testid="package-bar-jump"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-orange-dark underline decoration-orange/40 underline-offset-4 hover:decoration-orange transition-colors"
            >
              {remaining} answer{remaining === 1 ? "" : "s"} to go <ArrowRight size={13} />
            </button>
          ) : (
            <span className="text-xs font-semibold text-slate-500 text-right block max-w-[10rem]" data-testid="package-bar-status">
              Keep going
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

// Compact, sticky version shown alongside the questions.
export const QuoteRail = ({ quote, quoting, error, onSeePrice, emptyText, count = 0 }) => {
  // No figure here on purpose: a live total that moves as each answer is typed
  // is an invitation to price up individual elements. The number appears once,
  // on the price step.
  const ready = quote && (quote.status === "priced" || quote.status === "priced_review") && hasPrice(quote);
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm" data-testid="package-quote-rail">
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Your package</p>
        {quoting && <Loader2 size={14} className="animate-spin text-orange" />}
      </div>
      <p className="text-sm font-bold text-navy mt-2" data-testid="package-rail-count">
        {count} service{count === 1 ? "" : "s"} selected
      </p>
      {ready ? (
        <>
          <p className="text-sm text-navy mt-3 leading-relaxed font-semibold" data-testid="package-rail-ready">
            That's everything we need — your indicative price is ready.
          </p>
          <div className="mt-3"><Indicative /></div>
          <button
            type="button"
            onClick={onSeePrice}
            data-testid="package-rail-see-price"
            className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-navy hover:bg-navy-deep text-white font-bold text-sm px-4 py-3 rounded-full transition-colors"
          >
            See your price <ArrowRight size={15} />
          </button>
        </>
      ) : quote?.status === "consultation" ? (
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">
          Your answers point to a business we'd rather look at properly than price online.
        </p>
      ) : error ? (
        <p className="text-sm text-slate-600 mt-3 leading-relaxed">{error}</p>
      ) : (
        <p className="text-sm text-slate-500 mt-3 leading-relaxed">
          {emptyText || "Answer the questions and your indicative price will be ready at the end."}
        </p>
      )}
    </div>
  );
};

// Full step-3 presentation.
export const QuoteResult = ({ quote, quoting, error, onContinue, onBook, onBack, onStartOver, disclaimer, summaryHeading }) => {
  if (quoting && !quote) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center" data-testid="package-quote-loading">
        <Loader2 size={28} className="animate-spin text-orange mx-auto" />
        <p className="text-sm text-slate-500 mt-4">Working out your price…</p>
      </div>
    );
  }

  if (error && !quote) {
    return (
      <div className="rounded-2xl border border-orange/30 bg-orange/5 p-8" data-testid="package-quote-error">
        <AlertCircle className="text-orange" size={24} />
        <p className="font-display font-bold text-navy text-lg mt-3">We couldn't price that just now</p>
        <p className="text-sm text-slate-600 mt-2 leading-relaxed">{error}</p>
      </div>
    );
  }

  if (!quote) return null;

  if (quote.status === "consultation") {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid="package-quote-consultation">
        <div className="bg-navy text-white px-6 py-8 md:px-10">
          <h3 className="font-display font-extrabold text-2xl md:text-3xl leading-tight">
            {quote.consultation_title || "Your requirements need a little more thought"}
          </h3>
          <p className="text-slate-300 mt-4 leading-relaxed">
            {quote.consultation_message ||
              "Based on what you've told us, your requirements fall outside the circumstances we can accurately price online."}
          </p>
        </div>
        <div className="p-6 md:p-10">
          <p className="text-sm text-slate-600 leading-relaxed">
            Nothing is lost — everything you've told us comes with you, so the call starts from where you've got to rather than from
            scratch.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <button
              type="button"
              onClick={onBook}
              data-testid="package-consultation-book"
              className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
            >
              <CalendarClock size={17} /> Book a Discovery Call
            </button>
            <button
              type="button"
              onClick={onContinue}
              data-testid="package-consultation-continue"
              className="inline-flex items-center gap-2 border border-slate-300 hover:border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-colors"
            >
              Send us your details instead
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasPrice(quote)) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-8 md:p-10" data-testid="package-quote-needs-detail">
        <h3 className="font-display font-extrabold text-navy text-2xl">We need a little more to go on</h3>
        <p className="text-sm text-slate-600 mt-3 leading-relaxed max-w-xl">
          What you've told us so far doesn't yet describe enough of the work to price it honestly — usually it's the volume
          questions, like how many invoices you raise or how many accounts need reconciling. Fill in a few more and your price will
          appear straight away.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            type="button"
            onClick={onBack}
            data-testid="package-needs-detail-back"
            className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-7 py-3.5 rounded-full transition-all hover:-translate-y-0.5"
          >
            Add a few more details
          </button>
          <button
            type="button"
            onClick={onBook}
            data-testid="package-needs-detail-book"
            className="inline-flex items-center gap-2 border border-slate-300 hover:border-navy text-navy font-bold px-7 py-3.5 rounded-full transition-colors"
          >
            <CalendarClock size={17} /> Or just book a call
          </button>
        </div>
      </div>
    );
  }

  const totals = quote.totals || {};
  // One-off work on its own (a health check, say) has no monthly fee, so leading with
  // "£0.00 per month" reads as though nothing was priced. Lead with the real figure.
  const oneOffOnly = (totals.monthly_recurring || 0) === 0 && (totals.one_off || 0) > 0;
  return (
    <div data-testid="package-quote-result">
      <div className="rounded-2xl bg-navy text-white overflow-hidden">
        <div className="px-6 py-10 md:px-10 text-center">
          <Indicative />
          {oneOffOnly ? (
            <>
              <p className="text-slate-300 text-sm mt-4">Your indicative one-off fee</p>
              <p className="font-display font-extrabold text-5xl md:text-6xl mt-2 leading-none" data-testid="package-total-oneoff">
                {money(totals.one_off)}
              </p>
              <p className="text-slate-300 text-sm mt-3">one-off, + VAT</p>
              <p className="mt-6 inline-block bg-white/10 rounded-full px-5 py-2 text-sm text-white" data-testid="package-total-monthly">
                No monthly fee — there's nothing ongoing in this package
              </p>
            </>
          ) : (
            <>
              <p className="text-slate-300 text-sm mt-4">Your indicative monthly fee</p>
              <p className="font-display font-extrabold text-5xl md:text-6xl mt-2 leading-none" data-testid="package-total-monthly">
                {money(totals.monthly_recurring)}
              </p>
              <p className="text-slate-300 text-sm mt-3">per month, + VAT</p>
              {totals.one_off > 0 && (
                <p className="mt-6 inline-block bg-white/10 rounded-full px-5 py-2 text-sm text-white" data-testid="package-total-oneoff">
                  Plus a one-off {money(totals.one_off)} + VAT to get you set up
                </p>
              )}
            </>
          )}
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-4 leading-relaxed" data-testid="package-disclaimer">{disclaimer || DISCLAIMER}</p>

      {(quote.services || []).length > 0 && (
        <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-6 md:p-8" data-testid="package-summary">
          <h4 className="font-display font-bold text-navy text-lg">{summaryHeading || "What you've told us"}</h4>
          <div className="mt-5 space-y-5">
            {quote.services.map((s, i) => (
              <div key={i} className="border-t border-slate-100 pt-4 first:border-0 first:pt-0">
                <p className="font-bold text-navy text-sm">{s.name}</p>
                {(s.params || []).length > 0 && (
                  <dl className="mt-2 grid sm:grid-cols-2 gap-x-8 gap-y-1">
                    {s.params.map((p, j) => (
                      <div key={j} className="flex justify-between gap-4 text-xs">
                        <dt className="text-slate-500">{p.label}</dt>
                        <dd className="text-navy font-semibold text-right">{p.value}</dd>
                      </div>
                    ))}
                  </dl>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-8 flex flex-wrap gap-3">
        <button
          type="button"
          onClick={onContinue}
          data-testid="package-continue-to-details"
          className="inline-flex items-center gap-2 bg-orange hover:bg-orange-dark text-white font-bold px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
        >
          Talk it through with us <ArrowRight size={17} />
        </button>
        <button
          type="button"
          onClick={onBook}
          data-testid="package-quote-book"
          className="inline-flex items-center gap-2 border border-slate-300 hover:border-navy text-navy font-bold px-8 py-4 rounded-full transition-colors"
        >
          <CalendarClock size={17} /> Book a Discovery Call
        </button>
      </div>
    </div>
  );
};
